import { watch } from 'vue'
import type { WatchSource } from 'vue'
import { Temporal } from '@js-temporal/polyfill'

export abstract class AbstractSerializer {

  /**
   * Given a watched value, converts it to a string. Should return
   * null if this value is empty (and therefore should not be synced).
   */
  static serialize(value: any): null | string {
    throw new Error(`can't serialize ${value}: unimplemented`)
  }

  /**
   * Given a serialized string, converts it back to its original value.
   */
  static unserialize(value: any): any {
    throw new Error(`can't unserialize ${value}: unimplemented`)
  }

}

// export class DefaultSerializer extends AbstractSerializer {
//   static serialize(value) { return value }
//   static unserialize(value) { return value }
// }

export class BooleanSerializer extends AbstractSerializer {

  static serialize(value: boolean) {
    if (value === true) {
      return '1'
    } else if (value === false) {
      return '0'
    }
    return null
  }

  static unserialize(value: string): null | boolean {
    if (value === '1') {
      return true
    } else if (value === '0') {
      return false
    }
    return null
  }

}

export class DateSerializer extends AbstractSerializer {

  static #pattern = /^20[0-9]{2}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/

  static serialize(value: string) {
    return value
  }

  static unserialize(value: string): null | string {

    if (value === 'yesterday') {
      return Temporal.Now.plainDateISO().subtract({days: 1}).toString()
    }

    if (value === 'today') {
      return Temporal.Now.plainDateISO().toString()
    }

    if (value === 'tomorrow') {
      return Temporal.Now.plainDateISO().add({days: 1}).toString()
    }

    if (this.#pattern.test(value)) {
      const date = new Date(value)
      if (!Number.isNaN(date)) { // make sure it's actually a valid date
        return value
      }
    }
    return null
  }

}


export class NumberSerializer extends AbstractSerializer {

  static serialize(value: number) {
    if (value === null) {
      return null
    }
    return Number.isNaN(parseFloat(value.toString())) ? null : value.toString()
  }

  static unserialize(value: string): null | number {
    const unserializedValue = Number(value)
    return isNaN(unserializedValue) ? null : unserializedValue
  }

}

export class CommaSeparatedSerializer extends AbstractSerializer {

  static serialize(value: string[]) {
    return Array.isArray(value) && value.length ? value.join(',') : null
  }

  static unserialize(value: string) {
    return value.split(',')
  }

}

export class CommaSeparatedNumberSerializer extends AbstractSerializer {

  static serialize(value: number[]) {
    return value.length ? value.join(',') : null
  }

  static unserialize(value: string): number[] {
    return value.split(',').map((str) => parseInt(str, 10))
  }

}

/**
 * Utility class to update the querystring to reflect currently selected filters
 * (and to populate those values back into the filter form at page load time).
 *
 * I originally implemented this using vue-router's useRoute() and useRouter(),
 * but that led to issues. (Mainly, race conditions when more than one watcher
 * was triggered simultaneously.) Manipulating the querystring via the browser's
 * native APIs seems to work much better.
 */

interface QuerySyncerConfig {
  watchable: WatchSource
  hydrate: (newValue: any) => void
  serializer?: typeof AbstractSerializer
}

type QuerySyncerConfigs = {[key: string]: QuerySyncerConfig}

export class QuerySyncer {

  #configs: QuerySyncerConfigs = {}

  constructor(configs: QuerySyncerConfigs) {
    this.#configs = configs
    this.#hydrate()
    this.#registerWatchers()
  }

  /**
   * Copy values from the querystring into the state.
   */
  #hydrate() {

    Object.entries(this.#configs).forEach(([name, config]) => {
      const rawValue = this.#getQueryVar(name)
      if (rawValue) {
        const unserializedValue = config?.serializer ? config.serializer.unserialize(rawValue) : rawValue
        if (unserializedValue !== null) {
          config.hydrate(unserializedValue)
        }
      }
    })

  }

  #registerWatchers() {

    Object.entries(this.#configs).forEach(([name, config]) => {

      watch(
        config.watchable,
        (newValue) => {
          const newValueSerialized = config?.serializer ? config.serializer.serialize(newValue) : newValue
          if (newValueSerialized !== null && newValueSerialized !== '' && newValueSerialized !== undefined) {
            this.#setQueryVar(name, newValueSerialized)
          } else {
            this.#deleteQueryVar(name)
          }

        },
        {immediate: true}
      )

    })

  }

  #getQueryVar(name: string): null | string {
    return new URL(location.href).searchParams.get(name)
  }

  #setQueryVar(name: string, value: string): void {
    const url = new URL(location.href)
    url.searchParams.set(name, value)
    history.replaceState(history.state, '', url.toString())
  }

  #deleteQueryVar(name: string): void {
    const url = new URL(location.href)
    url.searchParams.delete(name)
    history.replaceState(history.state, '', url.toString())
  }

}